import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { Save, Eye, Edit3, Image, Type, Palette, Layout } from 'lucide-react';

const ContentEditor = () => {
  const { adminAPI } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [content, setContent] = useState({});
  const [preview, setPreview] = useState(false);

  const sections = [
    {
      id: 'hero',
      name: 'Section Héro',
      icon: <Layout className="w-4 h-4" />,
      fields: [
        { key: 'title', label: 'Titre principal', type: 'text', multiline: true },
        { key: 'subtitle', label: 'Sous-titre', type: 'text', multiline: true },
        { key: 'description', label: 'Description', type: 'textarea' },
        { key: 'buttonText', label: 'Texte du bouton', type: 'text' },
        { key: 'buttonSecondaryText', label: 'Texte du bouton secondaire', type: 'text' },
        { key: 'backgroundImage', label: 'Image de fond', type: 'image' },
      ]
    },
    {
      id: 'about',
      name: 'À Propos',
      icon: <Type className="w-4 h-4" />,
      fields: [
        { key: 'title', label: 'Titre de la section', type: 'text' },
        { key: 'subtitle', label: 'Sous-titre', type: 'text' },
        { key: 'description', label: 'Description principale', type: 'textarea' },
        { key: 'mission', label: 'Notre mission', type: 'textarea' },
        { key: 'experience', label: 'Années d\'expérience', type: 'number' },
        { key: 'projects', label: 'Nombre de projets', type: 'number' },
        { key: 'satisfaction', label: 'Taux de satisfaction', type: 'number' },
        { key: 'profileImage', label: 'Photo de profil', type: 'image' },
      ]
    },
    {
      id: 'services',
      name: 'Services',
      icon: <Edit3 className="w-4 h-4" />,
      fields: [
        { key: 'title', label: 'Titre de la section', type: 'text' },
        { key: 'subtitle', label: 'Sous-titre', type: 'text' },
        { key: 'description', label: 'Description', type: 'textarea' },
      ]
    },
    {
      id: 'projects',
      name: 'Projets',
      icon: <Image className="w-4 h-4" />,
      fields: [
        { key: 'title', label: 'Titre de la section', type: 'text' },
        { key: 'subtitle', label: 'Sous-titre', type: 'text' },
        { key: 'description', label: 'Description', type: 'textarea' },
      ]
    },
    {
      id: 'testimonials',
      name: 'Témoignages',
      icon: <Type className="w-4 h-4" />,
      fields: [
        { key: 'title', label: 'Titre de la section', type: 'text' },
        { key: 'subtitle', label: 'Sous-titre', type: 'text' },
        { key: 'description', label: 'Description', type: 'textarea' },
      ]
    },
    {
      id: 'contact',
      name: 'Contact',
      icon: <Edit3 className="w-4 h-4" />,
      fields: [
        { key: 'title', label: 'Titre de la section', type: 'text' },
        { key: 'subtitle', label: 'Sous-titre', type: 'text' },
        { key: 'description', label: 'Description', type: 'textarea' },
        { key: 'formTitle', label: 'Titre du formulaire', type: 'text' },
        { key: 'formDescription', label: 'Description du formulaire', type: 'textarea' },
        { key: 'successMessage', label: 'Message de succès', type: 'textarea' },
      ]
    },
    {
      id: 'footer',
      name: 'Pied de page',
      icon: <Layout className="w-4 h-4" />,
      fields: [
        { key: 'companyDescription', label: 'Description de l\'entreprise', type: 'textarea' },
        { key: 'copyright', label: 'Texte de copyright', type: 'text' },
        { key: 'privacyText', label: 'Texte confidentialité', type: 'text' },
        { key: 'termsText', label: 'Texte conditions', type: 'text' },
      ]
    },
    {
      id: 'general',
      name: 'Paramètres généraux',
      icon: <Palette className="w-4 h-4" />,
      fields: [
        { key: 'siteName', label: 'Nom du site', type: 'text' },
        { key: 'siteDescription', label: 'Description du site', type: 'textarea' },
        { key: 'siteKeywords', label: 'Mots-clés SEO', type: 'text' },
        { key: 'logo', label: 'Logo du site', type: 'image' },
        { key: 'favicon', label: 'Favicon', type: 'image' },
        { key: 'primaryColor', label: 'Couleur principale', type: 'color' },
        { key: 'secondaryColor', label: 'Couleur secondaire', type: 'color' },
        { key: 'accentColor', label: 'Couleur d\'accent', type: 'color' },
      ]
    }
  ];

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getContent();
      setContent(response.data.content || {});
    } catch (error) {
      console.error('Error loading content:', error);
      setContent({});
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await adminAPI.updateContent(content);
      alert('Contenu sauvegardé avec succès !');
    } catch (error) {
      console.error('Error saving content:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (sectionId, fieldKey, value) => {
    setContent(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        [fieldKey]: value
      }
    }));
  };

  const renderField = (sectionId, field) => {
    const value = content[sectionId]?.[field.key] || '';
    
    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleFieldChange(sectionId, field.key, e.target.value)}
            className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              field.multiline ? 'min-h-[100px]' : ''
            }`}
            placeholder={`Entrez ${field.label.toLowerCase()}...`}
          />
        );
      
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleFieldChange(sectionId, field.key, e.target.value)}
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical"
            placeholder={`Entrez ${field.label.toLowerCase()}...`}
          />
        );
      
      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleFieldChange(sectionId, field.key, parseInt(e.target.value) || 0)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder={`Entrez ${field.label.toLowerCase()}...`}
          />
        );
      
      case 'color':
        return (
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={value || '#000000'}
              onChange={(e) => handleFieldChange(sectionId, field.key, e.target.value)}
              className="w-16 h-12 border border-gray-300 rounded-lg cursor-pointer"
            />
            <input
              type="text"
              value={value}
              onChange={(e) => handleFieldChange(sectionId, field.key, e.target.value)}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="#000000"
            />
          </div>
        );
      
      case 'image':
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="url"
                value={value}
                onChange={(e) => handleFieldChange(sectionId, field.key, e.target.value)}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="URL de l'image ou chemin du fichier..."
              />
              <button
                type="button"
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg transition-colors"
                onClick={() => {
                  // TODO: Open media manager
                  alert('Gestionnaire de médias à implémenter');
                }}
              >
                Parcourir
              </button>
            </div>
            {value && (
              <div className="border border-gray-200 rounded-lg p-3">
                <img 
                  src={value} 
                  alt={field.label}
                  className="max-w-full h-32 object-contain rounded"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
        );
      
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Éditeur de Contenu</h1>
            <p className="text-gray-600">Modifiez tous les textes, images et couleurs de votre site</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setPreview(!preview)}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Eye className="w-4 h-4" />
              {preview ? 'Modifier' : 'Aperçu'}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900 mb-4">Sections</h3>
            <nav className="space-y-1">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-3 transition-colors ${
                    activeSection === section.id 
                      ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {section.icon}
                  <span className="text-sm font-medium">{section.name}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {sections.find(s => s.id === activeSection)?.name}
              </h2>
              <p className="text-gray-600 mt-1">
                Modifiez le contenu de cette section
              </p>
            </div>

            <div className="p-6">
              <div className="space-y-6">
                {sections.find(s => s.id === activeSection)?.fields.map((field) => (
                  <div key={field.key}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    {renderField(activeSection, field)}
                  </div>
                ))}
              </div>

              {/* Section Preview */}
              {content[activeSection] && Object.keys(content[activeSection]).length > 0 && (
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-3">Aperçu des données</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <pre className="text-sm text-gray-600 overflow-x-auto">
                      {JSON.stringify(content[activeSection], null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentEditor;